import { formatSessionDisplayName } from "./sessionDisplayName";
import { getEvidenceDocumentRoot } from "./reportEvidencePointer";

type SessionRow = { session_name?: string; session_id?: string };

function getSessionsArray(snapshot: unknown): SessionRow[] | null {
  const doc = getEvidenceDocumentRoot(snapshot);
  if (!doc || typeof doc !== "object") return null;
  const root = doc as Record<string, unknown>;
  const stats = root.stats as Record<string, unknown> | undefined;
  const sa = stats?.session_analysis as Record<string, unknown> | undefined;
  const sessions = sa?.sessions;
  if (!Array.isArray(sessions)) return null;
  return sessions as SessionRow[];
}

/**
 * Map array index → display label for stats.session_analysis.sessions (trade bundle / report snapshot).
 */
export function sessionIndexLabelsFromSnapshot(snapshot: unknown): Map<number, string> {
  const m = new Map<number, string>();
  const sessions = getSessionsArray(snapshot);
  if (!sessions) return m;
  sessions.forEach((row, i) => {
    const name = typeof row.session_name === "string" ? row.session_name : null;
    const id = typeof row.session_id === "string" ? row.session_id : null;
    const label = name
      ? formatSessionDisplayName(name)
      : id
        ? formatSessionDisplayName(id)
        : `Session ${i}`;
    m.set(i, label);
  });
  return m;
}

/**
 * Replace opaque indices like sessions/0 or stats/session_analysis/sessions/2 with human session names
 * from the report input snapshot (same ordering as bundle stats).
 */
export function enrichNarrativeSessionReferences(narrative: string, snapshot: unknown): string {
  const map = sessionIndexLabelsFromSnapshot(snapshot);
  if (map.size === 0 || !narrative) return narrative;

  const pattern =
    /\b(?:stats\/session_analysis\/sessions\/|session_analysis\/sessions\/|sessions\/)(\d+)\b/g;

  return narrative.replace(pattern, (_full, idxStr: string) => {
    const i = parseInt(idxStr, 10);
    if (Number.isNaN(i)) return _full;
    const label = map.get(i);
    return label != null ? label : _full;
  });
}
